// One agent's week, drawn as COVERAGE rather than as stored rows.
//
// The distinction is the whole point: an overnight shift is one row stored under
// the day it STARTS, so "Nights, Mon-Fri" (weekdays 1-5) covers Saturday morning
// and not Monday morning — and the other spelling covers Monday and not Saturday.
// Five identical bars either way, two different weeks.
//
// EVERY SENTENCE COMES FROM ./schedule-coverage.js, which is pure and tested
// (test/web/schedule-coverage.test.js). Nothing here decides what a day's coverage
// means — including, above all, what NO ROWS means. There is no DOM harness in this
// repository, so a ternary written inline is a ternary nobody can hold to being
// total, and the totality that matters here is not cosmetic: an agent with no rows
// is on shift NEVER, and a panel that implied otherwise would state the exact
// opposite of what the router does. (It once did mean 24/7, and this file drew a
// full-width filled bar to say so. Both the rule and the bar are gone; the empty
// week now draws empty and the summary beside it says what that costs.)
//
// COLOUR CARRIES NOTHING ON ITS OWN. Each day's bar is a picture of the same
// sentence printed beside it, so the panel is readable with the bars ignored,
// unrendered, or in greyscale. Both themes come out of semantic tokens; the one
// `dark:` variant is on the brand fill, where the two themes want different shades
// of the same hue rather than different roles.

import { weekCoverage, shiftNote, WEEKDAY_LABELS } from './schedule-coverage.js';

const MINUTES_IN_A_DAY = 24 * 60;

const percent = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (((h * 60) + m) / MINUTES_IN_A_DAY) * 100;
};

export default function ScheduleGrid({ rows, onChange }) {
  const week = weekCoverage(rows);

  const setRow = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <div className="mt-4 grid gap-6 lg:grid-cols-2">
      <div>
        <h3 className="text-sm font-semibold text-ink">Shifts</h3>
        <p className="mt-1 text-xs text-ink-muted">
          What is stored. A shift that ends before it starts runs overnight and belongs to the
          day it STARTS on.
        </p>
        <ul className="mt-2 divide-y divide-line rounded-lg border border-line bg-surface">
          {rows.length === 0 && (
            <li className="px-3 py-2 text-sm text-ink">
              No shifts. An agent with no shifts is never on shift, so routing will skip them
              and they will not be assigned any ticket. Add a shift to put them into rotation.
            </li>
          )}
          {rows.map((row, index) => {
            const note = shiftNote(row);
            return (
              <li key={`${row.weekday}-${row.start_time}-${row.end_time}-${index}`}
                  className="flex flex-wrap items-center gap-2 px-3 py-2">
                <select
                  aria-label="Day"
                  className="field-input w-auto"
                  value={row.weekday}
                  onChange={(event) => setRow(index, { weekday: Number(event.target.value) })}
                >
                  {WEEKDAY_LABELS.map((label, day) => (
                    <option key={label} value={day}>{label}</option>
                  ))}
                </select>
                {/* type="time" produces the 'HH:mm' string the API binds directly.
                    Never a Date: `mssql` sends one as T-SQL `datetime`, quantised
                    onto a 1/300-second grid. */}
                <input
                  aria-label="Start"
                  type="time"
                  className="field-input w-auto"
                  value={row.start_time}
                  onChange={(event) => setRow(index, { start_time: event.target.value })}
                />
                <span className="text-ink-muted">to</span>
                <input
                  aria-label="End"
                  type="time"
                  className="field-input w-auto"
                  value={row.end_time}
                  onChange={(event) => setRow(index, { end_time: event.target.value })}
                />
                <button
                  type="button"
                  className="btn-ghost ring-1 ring-line-strong"
                  onClick={() => onChange(rows.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
                {note && (
                  <span className="w-full text-xs text-ink-secondary">{note}</span>
                )}
              </li>
            );
          })}
        </ul>
        <button
          type="button"
          className="btn-ghost mt-2 ring-1 ring-line-strong"
          onClick={() => onChange([...rows, { weekday: 1, start_time: '09:00', end_time: '17:00' }])}
        >
          Add a shift
        </button>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-ink">Coverage</h3>
        <p className="mt-1 text-xs text-ink-muted">
          What those shifts actually cover. An overnight shift is stored under the day it starts,
          so Friday night appears on Saturday.
        </p>

        {/* The number nothing in this system reported until now: the live database's
            one schedule row buys its agent about 8 hours out of 168, and that agent
            is the only member of queue 1 who receives nothing. */}
        <p className="mt-2 text-sm text-ink">{week.summary}</p>

        <ul className="mt-2 divide-y divide-line rounded-lg border border-line bg-surface">
          <li className="flex items-center gap-3 px-3 py-1">
            <span className="w-20 shrink-0" />
            <span aria-hidden="true" className="flex flex-1 justify-between text-[10px] text-ink-muted">
              <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
            </span>
          </li>
          {week.days.map((day) => (
            <li key={day.label} className="flex items-center gap-3 px-3 py-2 text-sm">
              <span className="w-20 shrink-0 text-ink-secondary">{day.label}</span>
              {/* aria-hidden: the bar is a second rendering of `day.text`, which is
                  the accessible one. Announcing both reads every day twice. */}
              <span
                aria-hidden="true"
                className="relative h-3 flex-1 rounded-sm bg-sunken ring-1 ring-inset ring-line"
              >
                {/* There used to be a full-width filled bar here for a week with no
                    rows, because no rows meant on shift 24/7. It now means on shift
                    never, so an empty week draws as empty — which is what the bars
                    already do without help. The sentence explaining WHY it is empty
                    is `weekCoverage`'s `summary`, not a bar. */}
                {day.spans.map((span) => (
                  <span
                    key={`${span.from}-${span.to}`}
                    className="absolute inset-y-0 rounded-sm bg-brand-600 dark:bg-brand-500"
                    style={{
                      left: `${percent(span.from)}%`,
                      width: `${percent(span.to) - percent(span.from)}%`,
                    }}
                  />
                ))}
              </span>
              <span
                className={`w-48 shrink-0 text-right text-xs leading-snug ${
                  day.spans.length === 0 ? 'text-ink-muted' : 'text-ink'
                }`}
              >
                {day.text}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
